import type { SailPlotExtensionContext } from "@heiselanalytics/sailplot";
import { heiselLegalOperator } from "../config/legal";
import { paddleCustomerPortalUrl } from "../integrations/paddle";
import { configuratorLinkFromSuccessUrl } from "../integrations/purchaseSuccess";

const planNames = {
  en: {
    "personal-coach": "Personal, Coach & Sailing Team",
    "club-company": "Club & Company",
  },
  de: {
    "personal-coach": "Personal, Coach & Segelteam",
    "club-company": "Club & Company",
  },
} as const;

const copy = {
  en: {
    eyebrow: "Order confirmed",
    title: "Welcome aboard.",
    intro:
      "Your Paddle checkout is complete. Start the configurator to build your branded SailPlot.",
    ctaEyebrow: "Your next step",
    ctaTitle: "Continue to the configurator",
    ctaText:
      "Your purchase is already connected. Open your saved draft or start designing now.",
    packageLabel: "Selected package",
    packageFallback: "SailPlot branding & hosting",
    nextEyebrow: "What happens next",
    nextTitle: "From checkout to your board.",
    steps: [
      [
        "Check your confirmation",
        "Paddle sends your receipt. SailPlot sends a separate email with your personal configurator link and the next steps.",
      ],
      [
        "Open the configurator",
        "Use the secure button on this page to connect the configurator with your purchase.",
      ],
      [
        "Build your SailPlot",
        "Choose your logo, primary color, preferred URL, languages, links, and other branding options in the live preview.",
      ],
      [
        "Submit your design",
        "Send the finished configuration for review. We implement and check your design within 48 hours.",
      ],
    ],
    startConfigurator: "Start configurator",
    requestLink: "Request configurator link",
    manageBilling: "Manage subscription & invoices",
    manageBillingText:
      "View your subscription, update your payment method, or download invoices securely through Paddle.",
    cancel: "Cancel contract",
    withdraw: "Withdraw from contract",
    backToPlans: "Back to licenses & branding",
    openEditor: "Open editor",
    note: "The secure configurator link connects your design with the completed Paddle transaction.",
    mailSubject: "SailPlot configurator link",
    mailBody: `Hello Heisel Analytics,

I have purchased a SailPlot branding and hosting package.
Email address used for purchase:
Package:
Paddle transaction reference:

Please send me a new secure link to the SailPlot configurator.`,
  },
  de: {
    eyebrow: "Bestellung bestätigt",
    title: "Willkommen an Bord.",
    intro:
      "Dein Paddle-Checkout ist abgeschlossen. Starte jetzt den Konfigurator für dein gebrandetes SailPlot.",
    ctaEyebrow: "Dein nächster Schritt",
    ctaTitle: "Hier weiter zum Konfigurator",
    ctaText:
      "Dein Kauf ist bereits verbunden. Öffne deinen gespeicherten Entwurf oder starte jetzt mit der Gestaltung.",
    packageLabel: "Gewähltes Paket",
    packageFallback: "SailPlot Branding & Hosting",
    nextEyebrow: "So geht es weiter",
    nextTitle: "Vom Checkout zu deinem Board.",
    steps: [
      [
        "Bestätigung prüfen",
        "Paddle sendet deinen Beleg. SailPlot schickt dir zusätzlich eine eigene E-Mail mit deinem persönlichen Konfigurator-Link und den nächsten Schritten.",
      ],
      [
        "Konfigurator öffnen",
        "Verbinde den Konfigurator über den sicheren Button auf dieser Seite mit deinem Kauf.",
      ],
      [
        "SailPlot gestalten",
        "Wähle Logo, Primärfarbe, gewünschte URL, Sprachen, Links und weitere Branding-Optionen in der Live-Vorschau.",
      ],
      [
        "Design absenden",
        "Sende die fertige Konfiguration zur Prüfung. Wir setzen dein Design innerhalb von 48 Stunden um und prüfen es.",
      ],
    ],
    startConfigurator: "Konfigurator starten",
    requestLink: "Konfigurator-Link anfordern",
    manageBilling: "Abonnement & Rechnungen verwalten",
    manageBillingText:
      "Sieh dein Abonnement an, aktualisiere deine Zahlungsmethode oder lade Rechnungen sicher über Paddle herunter.",
    cancel: "Verträge hier kündigen",
    withdraw: "Vertrag widerrufen",
    backToPlans: "Zurück zu Lizenzen & Branding",
    openEditor: "Editor öffnen",
    note: "Der sichere Konfigurator-Link verbindet dein Design mit der abgeschlossenen Paddle-Transaktion.",
    mailSubject: "SailPlot Konfigurator-Link",
    mailBody: `Hallo Heisel Analytics,

ich habe ein SailPlot Branding- und Hosting-Paket gekauft.
Beim Kauf verwendete E-Mail-Adresse:
Paket:
Paddle-Transaktionsreferenz:

Bitte sende mir einen neuen sicheren Link zum SailPlot-Konfigurator.`,
  },
} as const;

const selectedPlan = (language: keyof typeof planNames) => {
  const planId = new URLSearchParams(window.location.search).get("plan");
  if (planId === "personal-coach" || planId === "club-company") {
    return planNames[language][planId];
  }
  return null;
};

export function PurchaseSuccessPage({
  language,
  navigate,
}: SailPlotExtensionContext) {
  const text = copy[language];
  const plan = selectedPlan(language);
  const configuratorUrl = configuratorLinkFromSuccessUrl(
    window.location.search,
  );
  const mailBody = plan
    ? text.mailBody
        .replace("Package:", `Package: ${plan}`)
        .replace("Paket:", `Paket: ${plan}`)
    : text.mailBody;
  const mailUrl = `mailto:${heiselLegalOperator.email}?${new URLSearchParams({
    subject: text.mailSubject,
    body: mailBody,
  }).toString()}`;

  return (
    <article className="platform-page platform-reference-page purchase-success-page">
      <header className="purchase-success-hero">
        <span className="purchase-success-check" aria-hidden="true">
          ✓
        </span>
        <div>
          <span className="platform-page-eyebrow">{text.eyebrow}</span>
          <h1>{text.title}</h1>
          <p>{text.intro}</p>
        </div>
      </header>

      {configuratorUrl ? (
        <a href={configuratorUrl} className="purchase-success-configurator-cta">
          <span>{text.ctaEyebrow}</span>
          <strong>{text.ctaTitle}</strong>
          <small>{text.ctaText}</small>
          <b aria-hidden="true">→</b>
        </a>
      ) : (
        <a href={mailUrl} className="purchase-success-configurator-cta">
          <span>{text.ctaEyebrow}</span>
          <strong>{text.requestLink}</strong>
          <small>{text.note}</small>
          <b aria-hidden="true">→</b>
        </a>
      )}

      <section className="purchase-success-billing">
        <div>
          <strong>{text.manageBilling}</strong>
          <span>{text.manageBillingText}</span>
        </div>
        <a
          className="customize-button customize-button--secondary"
          href={paddleCustomerPortalUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {text.manageBilling} ↗
        </a>
      </section>
      <div className="purchase-success-contract-actions">
        <button type="button" onClick={() => navigate("/cancel")}>
          {text.cancel}
        </button>
        <button type="button" onClick={() => navigate("/withdraw")}>
          {text.withdraw}
        </button>
      </div>

      <div className="purchase-success-package">
        <span>{text.packageLabel}</span>
        <strong>{plan ?? text.packageFallback}</strong>
      </div>

      <section className="purchase-success-next">
        <header>
          <span className="platform-page-eyebrow">{text.nextEyebrow}</span>
          <h2>{text.nextTitle}</h2>
        </header>
        <ol className="platform-instruction-grid">
          {text.steps.map(([title, description], index) => (
            <li key={title}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <strong>{title}</strong>
              <p>{description}</p>
            </li>
          ))}
        </ol>
      </section>

      <div className="purchase-success-actions">
        <button
          type="button"
          className="customize-button customize-button--secondary"
          onClick={() => navigate("/customize")}
        >
          {text.backToPlans}
        </button>
        <button
          type="button"
          className="customize-button customize-button--secondary"
          onClick={() => navigate("/editor")}
        >
          {text.openEditor}
        </button>
      </div>

      <aside className="platform-page-note">
        <p>{text.note}</p>
      </aside>
    </article>
  );
}
